import { V2_CURRENT_AREA_KEY } from '../persistence/persistenceContract';
import { createStore } from './createStore';
import type { InspectionState } from './inspectionStore';

// Where the inspector IS in each inspection: the area whose workspace was opened last. A device-
// local convenience under the yanwushi-v2 localStorage namespace — never authority, never part of a
// report, never synced. It decides only two things: which area card the Inspection Overview
// outlines and where Resume goes — so leaving the app in the middle of the kitchen and coming back
// lands in the kitchen, not on the first area of the house (Owner device test 2026-09-12).
export interface AreaFocusEntry {
  areaId: string;
  at: number;
}

export interface AreaFocusState {
  byInspection: Record<string, AreaFocusEntry>;
}

function readStored(): Record<string, AreaFocusEntry> {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(V2_CURRENT_AREA_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, AreaFocusEntry> = {};
    for (const [inspectionId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const { areaId, at } = value as { areaId?: unknown; at?: unknown };
      if (typeof areaId === 'string' && areaId.length > 0) out[inspectionId] = { areaId, at: typeof at === 'number' && Number.isFinite(at) ? at : 0 };
    }
    return out;
  } catch {
    return {};
  }
}

function writeStored(byInspection: Record<string, AreaFocusEntry>): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(V2_CURRENT_AREA_KEY, JSON.stringify(byInspection));
  } catch {
    // Storage blocked or full: the in-memory copy still serves this session.
  }
}

export const areaFocusStore = createStore<AreaFocusState>({ byInspection: readStored() });

// Called when an area workspace is shown. Idempotent: re-rendering the same area writes nothing.
export function rememberCurrentArea(inspectionId: string, areaId: string, now: number = Date.now()): void {
  areaFocusStore.set((prev) => {
    if (prev.byInspection[inspectionId]?.areaId === areaId) return prev;
    const byInspection = { ...prev.byInspection, [inspectionId]: { areaId, at: now } };
    writeStored(byInspection);
    return { byInspection };
  });
}

export function rememberedAreaId(focus: AreaFocusState, inspectionId: string): string | null {
  return focus.byInspection[inspectionId]?.areaId ?? null;
}

// The area Resume opens and the overview outlines: the remembered area while it still exists in this
// inspection, is enabled and is not completed; otherwise the caller's fallback (the area of the next
// uninspected item in inspection order — the rule that stood alone before 2026-09-12).
export function resumeAreaIdFor(focus: AreaFocusState, state: InspectionState, inspectionId: string, fallbackAreaId: string | null): string | null {
  const remembered = rememberedAreaId(focus, inspectionId);
  if (remembered) {
    const area = state.areas.find((a) => a.id === remembered && a.inspectionId === inspectionId);
    if (area && area.enabled && area.completedAt === null) return remembered;
  }
  return fallbackAreaId;
}

export function resetAreaFocusForTests(): void {
  areaFocusStore.set(() => ({ byInspection: {} }));
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(V2_CURRENT_AREA_KEY);
  } catch {
    // nothing stored
  }
}
